import { supabase } from "@/lib/supabase";
import type { Trip } from "@/context/JourneyContext";

// Server-side trip persistence (Supabase `trips` table). All calls are
// scoped to the signed-in user by Row Level Security (the policies on the
// table enforce user_id = auth.uid()), so we pass user_id on write and
// trust RLS on read. The whole Trip object lives in the `data` JSONB
// column; title/destination are promoted out for cheap listing.
//
// These are the SERVER path only. JourneyContext decides per-call whether
// to use these (signed in) or localStorage (anonymous). Every function is
// a no-op-safe null when supabase isn't configured.

// Map a DB row back to the app Trip. The full object is in `data`; we trust
// it but fall back to the promoted columns / id if needed.
function rowToTrip(row: { id: string; data: Trip }): Trip {
  return { ...row.data, id: row.id };
}

export async function listTrips(): Promise<Trip[]> {
  if (!supabase) return [];
  // Confirm the request will carry an auth token. If the session hasn't
  // hydrated yet, a read would run as `anon`, which RLS filters to zero rows
  // (no error, just empty) and would look like "all my trips vanished".
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    console.warn("trip-store.listTrips: no active session yet; skipping read.");
    return [];
  }
  const { data, error } = await supabase
    .from("trips")
    .select("id, data")
    .order("updated_at", { ascending: false });
  if (error) {
    console.error("trip-store.listTrips failed:", error.message);
    return [];
  }
  console.info(`trip-store.listTrips: ${data?.length ?? 0} trips for user.`);
  return (data ?? []).map(rowToTrip);
}

// Insert or update one trip for the given user. user_id must be passed
// (RLS checks it matches auth.uid()).
export async function upsertTrip(trip: Trip, userId: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from("trips").upsert(
    {
      id: trip.id,
      user_id: userId,
      title: trip.title ?? null,
      destination: trip.destination ?? null,
      data: trip,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "id" }
  );
  if (error) {
    console.error("trip-store.upsertTrip failed:", error.message);
    throw error;
  }
}

export async function deleteTrip(id: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from("trips").delete().eq("id", id);
  if (error) {
    console.error("trip-store.deleteTrip failed:", error.message);
    throw error;
  }
}

export async function deleteAllTrips(): Promise<void> {
  if (!supabase) return;
  // RLS scopes this to the current user; the neq guard satisfies the
  // client's requirement for a filter on bulk delete.
  const { error } = await supabase
    .from("trips")
    .delete()
    .neq("id", "00000000-0000-0000-0000-000000000000");
  if (error) {
    console.error("trip-store.deleteAllTrips failed:", error.message);
    throw error;
  }
}
